import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
  VictoryChart,
  VictoryAxis,
  VictoryLine,
  VictoryArea,
  VictoryTheme,
  VictoryLegend,
  VictoryTooltip,
  VictoryVoronoiContainer,
} from "victory";

// Constants for chart styling and configuration
const COLORS = {
  assets: "#0072B2",
  debts: "#D55E00",
  netWorth: "#009E73",
  assetsCI: "#56B4E9",
  debtsCI: "#E69F00",
};

// Utility functions
const parseDate = (d) => (typeof d === "string" ? new Date(d) : d);
const formatCurrency = (val) =>
  "$" + parseFloat(val).toLocaleString(undefined, { maximumFractionDigits: 0 });

/**
 * NetWorthTrend - A component for visualizing historical and projected net worth data
 */
const BalanceSheetNetWorthTrend = ({
  projectionWithCI = [],
  historicalAssets = [],
  projectedAssets = [],
  historicalDebts = [],
  projectedDebts = [],
  debtsProjectionWithCI = [],
  historicalNetWorth = [],
  projectedNetWorth = [],
  lastHistYear,
  onShowAssumptions,
  children,
}) => {
  // Find the earliest date from all historical data
  const earliestDate = useMemo(() => {
    const allDates = [
      ...historicalAssets.map((d) => new Date(d.start_date)),
      ...historicalDebts.map((d) => new Date(d.start_date)),
    ];
    return allDates.length > 0 ? new Date(Math.min(...allDates)) : null;
  }, [historicalAssets, historicalDebts]);

  // Generate years for x-axis ticks
  const xAxisYears = useMemo(() => {
    if (!earliestDate) return [];

    const startYear = earliestDate.getFullYear();
    const currentYear = new Date().getFullYear();
    const endYear = currentYear + 25;
    const roundedEndYear = Math.ceil((endYear - startYear) / 5) * 5 + startYear;

    // Start with the earliest data year and current year
    const years = new Set([startYear, currentYear]);

    // Add 5-year increments from the first multiple of 5 after start year
    const firstFiveYear = Math.ceil(startYear / 5) * 5;
    for (let year = firstFiveYear; year <= roundedEndYear; year += 5) {
      if (year > startYear) {
        years.add(year);
      }
    }

    // Convert years to dates and sort
    return Array.from(years)
      .sort((a, b) => a - b)
      .map((year) => new Date(year, 0, 1));
  }, [earliestDate]);

  // Transform and combine asset data to ensure smooth transition
  const allAssetPoints = useMemo(() => {
    // Sort historical assets by date
    const sortedHistorical = [...historicalAssets]
      .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
      .map((d) => ({
        x: parseDate(d.start_date),
        y: d.value,
        isHistorical: true,
      }));

    // Get the last historical point
    const lastHistoricalAsset = sortedHistorical[sortedHistorical.length - 1];

    // Sort projected assets by date
    const sortedProjected = [...projectedAssets]
      .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
      .map((d) => ({
        x: parseDate(d.start_date),
        y: d.value,
        isProjected: true,
      }));

    // If we have both historical and projected data, ensure smooth transition
    if (lastHistoricalAsset && sortedProjected.length > 0) {
      return [
        ...sortedHistorical,
        {
          x: lastHistoricalAsset.x,
          y: lastHistoricalAsset.y,
          isTransition: true,
        },
        ...sortedProjected,
      ];
    }

    return [...sortedHistorical, ...sortedProjected];
  }, [historicalAssets, projectedAssets]);

  // Split asset points into historical and projected for rendering
  const historicalAssetPoints = useMemo(
    () => allAssetPoints.filter((p) => p.isHistorical || p.isTransition),
    [allAssetPoints],
  );

  const projectedAssetPoints = useMemo(
    () => allAssetPoints.filter((p) => p.isProjected || p.isTransition),
    [allAssetPoints],
  );

  // Transform and combine debt data to ensure smooth transition
  const allDebtPoints = useMemo(() => {
    // Sort historical debts by date
    const sortedHistorical = [...historicalDebts]
      .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
      .map((d) => ({
        x: parseDate(d.start_date),
        y: d.value,
        isHistorical: true,
      }));

    // Get the last historical point
    const lastHistoricalDebt = sortedHistorical[sortedHistorical.length - 1];

    // Sort projected debts by date
    const sortedProjected = [...projectedDebts]
      .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
      .map((d) => ({
        x: parseDate(d.start_date),
        y: d.value,
        isProjected: true,
      }));

    // If we have both historical and projected data, ensure smooth transition
    if (lastHistoricalDebt && sortedProjected.length > 0) {
      return [
        ...sortedHistorical,
        {
          x: lastHistoricalDebt.x,
          y: lastHistoricalDebt.y,
          isTransition: true,
        },
        ...sortedProjected,
      ];
    }

    return [...sortedHistorical, ...sortedProjected];
  }, [historicalDebts, projectedDebts]);

  // Split debt points into historical and projected for rendering
  const historicalDebtPoints = useMemo(
    () => allDebtPoints.filter((p) => p.isHistorical || p.isTransition),
    [allDebtPoints],
  );

  const projectedDebtPoints = useMemo(
    () => allDebtPoints.filter((p) => p.isProjected || p.isTransition),
    [allDebtPoints],
  );

  // Calculate net worth points (assets - debts)
  const netWorthPoints = useMemo(() => {
    const allDates = new Set([
      ...allAssetPoints.map((p) => p.x.getTime()),
      ...allDebtPoints.map((p) => p.x.getTime()),
    ]);

    return Array.from(allDates)
      .sort((a, b) => a - b)
      .map((timestamp) => {
        const date = new Date(timestamp);
        const asset =
          allAssetPoints.find((p) => p.x.getTime() === timestamp)?.y || 0;
        const debt =
          allDebtPoints.find((p) => p.x.getTime() === timestamp)?.y || 0;
        return {
          x: date,
          y: asset - debt,
          isProjected: date > new Date(lastHistYear, 11, 31),
        };
      });
  }, [allAssetPoints, allDebtPoints, lastHistYear]);

  // Transform confidence interval data
  const ciArea = useMemo(
    () =>
      projectionWithCI.map((d) => ({
        x: parseDate(d.date),
        y0: d.assetsLow,
        y: d.assetsHigh,
      })),
    [projectionWithCI],
  );

  // Add today's date constant
  const today = useMemo(() => new Date(), []);

  // Early return if no data
  if (!historicalAssets.length && !projectedAssets.length) {
    return (
      <div
        style={{
          height: "400px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div className="text-gray-500 dark:text-gray-500">No trend data available</div>
      </div>
    );
  }

  // Calculate domain boundaries
  const allPoints = [...allAssetPoints, ...allDebtPoints];

  const allY = allPoints
    .map((p) => p.y)
    .concat(
      ciArea.map((p) => p.y0),
      ciArea.map((p) => p.y),
    )
    .filter((y) => !isNaN(y));

  const minY = Math.min(...allY) * 0.98;
  const maxY = Math.max(...allY) * 1.02;

  // Calculate x-axis domain
  const allX = allPoints.map((p) => p.x.getTime());
  const xDomain = earliestDate
    ? {
        x: [
          earliestDate,
          Math.max(...allX, ...ciArea.map((p) => p.x.getTime())),
        ],
      }
    : undefined;

  return (
    <div className="relative" style={{ height: "370px" }}>
      <div className="flex justify-between items-center mb-1">
        <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Net Worth Trend</h4>
        {onShowAssumptions && (
          <button
            onClick={onShowAssumptions}
            className="text-blue-600 hover:text-blue-800 flex items-center"
          >
            <span className="mr-1">View Assumptions</span>
          </button>
        )}
      </div>
      <VictoryChart
        theme={VictoryTheme.material}
        domainPadding={{ x: 0 }}
        padding={{ top: 30, left: 50, right: 10, bottom: 30 }}
        height={320}
        width={550}
        scale={{ x: "time", y: "linear" }}
        domain={{ y: [minY, maxY], ...xDomain }}
        containerComponent={
          <VictoryVoronoiContainer
            voronoiBlacklist={["vertical-line"]}
            labels={({ datum, series }) =>
              `${series || "Value"}\n${datum.x.toLocaleDateString()}\n${formatCurrency(datum.y)}`
            }
            labelComponent={
              <VictoryTooltip
                cornerRadius={4}
                flyoutStyle={{ fill: "white" }}
              />
            }
          />
        }
      >
        <VictoryLegend
          x={50}
          y={0}
          orientation="horizontal"
          gutter={12}
          symbolSpacer={4}
          style={{
            labels: { fontSize: 9 },
            data: { size: 6 },
          }}
          data={[
            { name: "Assets", symbol: { fill: COLORS.assets } },
            { name: "Debts", symbol: { fill: COLORS.debts } },
            { name: "Net Worth", symbol: { fill: COLORS.netWorth } },
            {
              name: "Projection Range",
              symbol: { fill: COLORS.assetsCI, type: "square" },
            },
          ]}
        />

        {/* Add vertical line for today's date */}
        <VictoryLine
          name="vertical-line"
          data={[
            { x: today, y: minY },
            { x: today, y: maxY },
          ]}
          style={{
            data: { stroke: "#000000", strokeWidth: 1 },
          }}
          standalone={false}
        />

        {/* Confidence Interval */}
        {ciArea.length > 0 && (
          <VictoryArea
            data={ciArea}
            x="x"
            y="y"
            y0="y0"
            style={{ data: { fill: COLORS.assetsCI, opacity: 0.15 } }}
          />
        )}

        {/* Historical Assets */}
        <VictoryLine
          data={historicalAssetPoints}
          style={{ data: { stroke: COLORS.assets, strokeWidth: 2 } }}
        />

        {/* Projected Assets */}
        <VictoryLine
          data={projectedAssetPoints}
          style={{
            data: {
              stroke: COLORS.assets,
              strokeWidth: 2,
              strokeDasharray: "4,4",
            },
          }}
        />

        {/* Historical Debts */}
        <VictoryLine
          data={historicalDebtPoints}
          style={{ data: { stroke: COLORS.debts, strokeWidth: 2 } }}
        />

        {/* Projected Debts */}
        <VictoryLine
          data={projectedDebtPoints}
          style={{
            data: {
              stroke: COLORS.debts,
              strokeWidth: 2,
              strokeDasharray: "4,4",
            },
          }}
        />

        {/* Net Worth Line */}
        <VictoryLine
          data={netWorthPoints}
          style={{
            data: {
              stroke: COLORS.netWorth,
              strokeWidth: 2,
              strokeDasharray: (d) => (d.isProjected ? "4,4" : undefined),
            },
          }}
        />

        <VictoryAxis
          tickValues={xAxisYears}
          tickFormat={(x) => x.getFullYear()}
          style={{
            tickLabels: { fontSize: 9, padding: 1 },
            grid: { stroke: "none" },
          }}
        />
        <VictoryAxis
          dependentAxis
          tickFormat={formatCurrency}
          style={{
            tickLabels: { fontSize: 9, padding: 1 },
            grid: { stroke: "none" },
          }}
        />
      </VictoryChart>
      {children}
    </div>
  );
};

BalanceSheetNetWorthTrend.propTypes = {
  projectionWithCI: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      assetsHigh: PropTypes.number.isRequired,
      assetsLow: PropTypes.number.isRequired,
    }),
  ),
  historicalAssets: PropTypes.arrayOf(
    PropTypes.shape({
      start_date: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    }),
  ),
  projectedAssets: PropTypes.arrayOf(
    PropTypes.shape({
      start_date: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    }),
  ),
  historicalDebts: PropTypes.arrayOf(
    PropTypes.shape({
      start_date: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    }),
  ),
  projectedDebts: PropTypes.arrayOf(
    PropTypes.shape({
      start_date: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    }),
  ),
  debtsProjectionWithCI: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      debtsHigh: PropTypes.number.isRequired,
      debtsLow: PropTypes.number.isRequired,
    }),
  ),
  historicalNetWorth: PropTypes.arrayOf(
    PropTypes.shape({
      start_date: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    }),
  ),
  projectedNetWorth: PropTypes.arrayOf(
    PropTypes.shape({
      start_date: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    }),
  ),
  lastHistYear: PropTypes.number,
  onShowAssumptions: PropTypes.func,
  children: PropTypes.node,
};

export default BalanceSheetNetWorthTrend;
